import { useMemo, useState, useCallback } from "react";
import { DataContext } from "context/Data/context";
import axios from "../../utils/axios";
import { API_URL } from "../../constants";

export const DataState = ({ children }) => {
  const [dataFromStore, setList] = useState({});
  const [item, setItem] = useState({});

  const getList = useCallback(
    async (
      entity: string,
      insteadName?: string,
      set = true,
      withoutLoading = false,
      signal?: AbortSignal
    ) => {
      const { data } = await axios.get(`/${entity}`, {
        headers: { common: { withoutLoading } },
        signal,
      });
      if (data) {
        set &&
          setList((prevDataFromStore) => ({
            ...prevDataFromStore,
            [insteadName ? insteadName : entity]: data,
          }));
        return data;
      }
    },
    []
  );

  const getItem = useCallback(async (entity, id) => {
    const { data } = await axios.get(`/${entity}/${id}`);
    if (data) {
      setItem(data);
    }

    return data;
  }, []);

  const createFormDataItem = useCallback(
    async (entity, newData, updated = true, insteadName) => {
      const formData = new FormData();
      for (const field in newData) {
        if (
          newData[field] instanceof Array &&
          newData[field].every((element) => element instanceof File)
        ) {
          newData[field].forEach((file) => formData.append(field, file));
        } else {
          formData.append(field, newData[field]);
        }
      }
      const { status, data } = await axios.post(`/${entity}`, formData);
      if (updated && (status === 201 || status === 200)) {
        getList(entity, insteadName);
      }
      return data;
    },
    [getList]
  );

  const createItem = useCallback(
    async (entity, newData, set = true, withoutLoading = false) => {
      const { status, data } = await axios.post(`/${entity}`, newData, {
        headers: { common: { withoutLoading } },
      });
      if (set && status === 201) {
        getList(entity);
      }
      return data;
    },
    [getList]
  );

  const deleteItem = useCallback(async (entity, id, set = true, data) => {
    const { status } = await axios.delete(`/${entity}/${id}`, {
      data,
    });
    if (status === 204 && set) {
      setList((prevDataFromStore) => ({
        ...prevDataFromStore,
        [entity]: prevDataFromStore[entity].filter((b) => b.id !== id),
      }));
    }
  }, []);

  const deleteCategoryItem = useCallback(
    async (entity, id, set = true, isDeletedConfirm) => {
      const { data } = await axios.delete(
        `/${entity}/${id}?isDeletedConfirm=${isDeletedConfirm}`
      );
      return data;
    },
    []
  );

  const deleteItems = useCallback(async (entity, payload) => {
    await axios.delete(`/${entity}`, { data: payload });
  }, []);

  const updateFormDataItem = useCallback(
    async (
      entity,
      updateItem,
      id,
      updated = true,
      insteadName,
      withoutLoading = false
    ) => {
      const formData = new FormData();
      for (const field in updateItem) {
        if (
          updateItem[field] instanceof Array &&
          updateItem[field].every((element) => element instanceof File)
        ) {
          updateItem[field].forEach((file) => formData.append(field, file));
        } else {
          formData.append(field, updateItem[field]);
        }
      }
      const { status } = await axios.put(`/${entity}/${id}`, formData, {
        headers: { common: { withoutLoading } },
      });
      if (updated && status === 200) {
        getList(entity, insteadName);
      }
    },
    [getList]
  );

  const updateItem = useCallback(
    async (entity, updateItem, id, set = true, insteadName) => {
      const { status } = await axios.put(`/${entity}/${id}`, updateItem);
      if (status === 200 && set) {
        getList(entity, insteadName);
      }
    },
    [getList]
  );

  const updateAllItem = useCallback(
    async (entity, updateItem, set = true, insteadName) => {
      const { status } = await axios.put(`/${entity}`, updateItem);
      if (status === 200 && set) {
        getList(entity, insteadName);
      }
    },
    [getList]
  );

  const updatePartner = useCallback(
    async (entity, updateItem, id, userId) => {
      const { status } = await axios.put(`/${entity}/${id}`, updateItem);
      if (status === 200) {
        getItem("partner/user", userId);
      }
    },
    [getItem]
  );

  const updateOrder = useCallback(
    async (entity, updateItem, id) => {
      const { status } = await axios.put(`/${entity}/${id}`, updateItem);
      if (status === 200 && updateItem.state !== 3) {
        getItem("order", id);
      }
    },
    [getItem]
  );

  const updateOrderDetail = useCallback(
    async (entity, updateItem, id, orderId) => {
      const { status } = await axios.put(`/${entity}/${id}`, updateItem);
      if (status === 200) {
        getList(`orderDetail/order/${orderId}`, "products");
        getItem("order", orderId);
      }
    },
    [getList, getItem]
  );

  const addProductToOrder = useCallback(
    async (entity, newData) => {
      const { status } = await axios.post(`/${entity}`, newData);
      if (status === 201) {
        getList(`orderDetail/order/${newData.order}`, "products");
        getItem("order", newData.order);
      }
    },
    [getList, getItem]
  );

  const deleteProductFromOrder = useCallback(
    async (id, orderId) => {
      const { status } = await axios.delete(`/orderDetail/${id}`);
      if (status === 204) {
        setList((prevDataFromStore) => ({
          ...prevDataFromStore,
          products: prevDataFromStore.products.filter((b) => b.id !== id),
        }));
        getItem("order", orderId);
      }
    },
    [getItem]
  );

  const fetchImage = useCallback(async (imageUrls, headers) => {
    const responses = [];

    for (let i = 0; i < imageUrls.length; i++) {
      if (!imageUrls[i]) continue;
      const resp = await fetch(
        `${API_URL}/private/img/${imageUrls[i]}`,
        headers
      );
      responses[i] = URL.createObjectURL(await resp.blob());
    }

    return responses;
  }, []);

  const fetchFile = useCallback(async (url) => {
    const { data } = await axios.get(url, {
      responseType: "blob",
    });
    return data;
  }, []);

  const updateGlobalSettings = useCallback(
    async (data) => {
      const { status } = await axios.put(`/globalSetting`, data);
      if (status === 200) {
        getList("globalSetting");
      }
    },
    [getList]
  );

  const updatePartnerSettings = useCallback(
    async (data, id) => {
      const { status } = await axios.put(`/partner/${id}`, data);
      if (status === 200) {
        getItem("partner", id);
      }
    },
    [getItem]
  );

  const addProductsToRequest = useCallback(
    async (newData, partnerId) => {
      const { status } = await axios.post(`/supplyRequest`, newData);
      if (status === 201) {
        getList(`supplyRequest?partner=${partnerId}`, "supplyRequest");
      }
    },
    [getList]
  );

  const updateRequestAmount = useCallback(
    async (entity, updateItem, id, partnerId, updateList = true) => {
      const { status } = await axios.put(`/${entity}/${id}`, updateItem);
      if (status >= 200 && updateList) {
        getList(
          `${entity}?${
            entity.includes("supply") ? "partner" : "currierId"
          }=${partnerId}`,
          entity
        );
      }
    },
    [getList]
  );

  const addProductsToCourier = useCallback(
    async (newData, courierId) => {
      const { status } = await axios.post(`/currierProduct`, newData);
      if (status === 201) {
        getList(`currierProduct?currierId=${courierId}`, "currierProduct");
      }
    },
    [getList]
  );

  const updateUser = useCallback(async (id, data) => {
    await axios.put(`/user/${id}`, data);
  }, []);

  const resetPartnerPassword = useCallback(async (email) => {
    return await axios.put(`/user/resetPassword`, { email });
  }, []);

  const updateMarkAsSeen = useCallback(async (orderIdList = []) => {
    await axios.put(`/order/view`, {
      orders: orderIdList,
    });
  }, []);

  const getPerkActivated = useCallback(async (id) => {
    const { data } = await axios.get(`/nft-perk/${id}/activated`);
    return data;
  }, []);

  const uploadXlsId = useCallback(async (entity, id, files) => {
    const formData = new FormData();
    for (const field in files) {
      if (
        files[field] instanceof Array &&
        files[field].every((element) => element instanceof File)
      ) {
        files[field].forEach((file) => formData.append(field, file));
      } else {
        formData.append(field, files[field]);
      }
    }
    const { data } = await axios.post(`/${entity}/${id}`, formData);
    return data;
  }, []);

  const publishItem = useCallback(async (entity, updateItem, id) => {
    await axios.put(`/${entity}/${id}/publish`, updateItem);
  }, []);

  const getGame = useCallback(async (id) => {
    const { data } = await axios.get(`/ar-sticker-game/${id}/users`);
    return data;
  }, []);

  const deleteUserGame = useCallback(async (gameId, userId) => {
    try {
      await axios.delete(`/ar-sticker-game/user/${userId}/${gameId}`);
    } catch (err) {
      return Promise.reject(err);
    }
  }, []);

  const getCurrentWorker = useCallback(async () => {
    try {
      const { data } = await axios.get(`/worker/current`);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  }, []);

  const getNotificationHistory = useCallback(
    async (userId, brandId, type, withoutLoading = false) => {
      try {
        const { data } = await axios.get(
          `/customer-notification/user/${userId}/brand/${brandId}/type/${type}`,
          {
            headers: { common: { withoutLoading } },
          }
        );
        return data;
      } catch (err) {
        return Promise.reject(err);
      }
    },
    []
  );

  const updateUsersTags = useCallback(async (path) => {
    await axios.put(path);
  }, []);

  const deleteUserTag = useCallback(async ({ userId, tagId }) => {
    try {
      await axios.delete(`/customer-tag/${tagId}/user/${userId}`);
    } catch (err) {
      return Promise.reject(err);
    }
  }, []);

  const value = useMemo(
    () => ({
      dataFromStore,
      setList,
      item,
      getItem,
      getList,
      createItem,
      deleteItem,
      deleteItems,
      updateItem,
      updateFormDataItem,
      createFormDataItem,
      fetchImage,
      fetchFile,
      updateOrder,
      addProductToOrder,
      updateOrderDetail,
      deleteProductFromOrder,
      updateAllItem,
      updatePartner,
      updateGlobalSettings,
      updatePartnerSettings,
      addProductsToRequest,
      updateRequestAmount,
      addProductsToCourier,
      resetPartnerPassword,
      updateUser,
      updateMarkAsSeen,
      getPerkActivated,
      uploadXlsId,
      publishItem,
      getGame,
      deleteUserGame,
      deleteCategoryItem,
      getCurrentWorker,
      getNotificationHistory,
      updateUsersTags,
      deleteUserTag,
    }),
    // eslint-disable-next-line
    [dataFromStore, item]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
